import { ChartOptions } from 'highcharts';
import i18n from 'i18n';
import { BlazeMetricTypes } from 'store/analytics/data/analyticsDataTypes';
import { MsType, TRANSACTIONAL } from 'types/rest/mockServiceTypes';

export const FONT_COLOR = '#58606e';
export const WHITE = '#FFFFFF';
export const PLOT_BACKGROUND_COLOR = '#F8FAFC';
export const PLOT_BORDER_COLOR = '#cfcfcf';
export const GRAY_LEGEND = '#58606e';
export const GRAY_LABEL = '#58606e';
export const PURPLE_PLOT = '#614385';
export const BLUE_PLOT = '#516395';
export const GRID_BORDER_COLOR = '#C8D8E2';
export const PLOT_COLORS = [
  [0, PURPLE_PLOT],
  [1, BLUE_PLOT],
];

export type ChartColor = {
  color: string;
  border: string;
};

/* AC Colors */
export const BLUE_LIGHT = '#d1d9f8';
export const BLUE_DARK = '#95a8f3';
export const BLUE_DARK_ALT = '#5a78ef';

export const ORANGE_LIGHT = '#f0e3c9';
export const ORANGE_DARK = '#e9cc97';

export const GREEN_LIGHT = '#d9ebcb';
export const GREEN_DARK = '#aad583';
export const GREEN_DARK_ALT = '#7bbf3d';

export const YELLOW_LIGHT = '#ffffe6';
export const YELLOW_DARK = '#b3b36b';
export const YELLOW_DARK_ALT = '#d89f34';

// Pair of colors for chart
export const BAR_CHART_BLUE: ChartColor = { color: BLUE_LIGHT, border: BLUE_DARK };
export const BAR_CHART_ORANGE: ChartColor = { color: ORANGE_LIGHT, border: ORANGE_DARK };
export const BAR_CHART_GREEN: ChartColor = { color: GREEN_LIGHT, border: GREEN_DARK };
export const BAR_CHART_YELLOW: ChartColor = { color: GREEN_LIGHT, border: GREEN_DARK };
export const LINE_CHART_YELLOW: ChartColor = { color: YELLOW_DARK_ALT, border: YELLOW_DARK_ALT };
export const LINE_CHART_BLUE: ChartColor = { color: BLUE_DARK_ALT, border: BLUE_DARK_ALT };
export const LINE_CHART_GREEN: ChartColor = { color: GREEN_DARK_ALT, border: GREEN_DARK_ALT };

/**
 * Determines graph color based on metric type.
 * @param type
 */
export const getGraphColor = (type: string) => {
  switch (type) {
    case BlazeMetricTypes.RESPONSE_TIME_MIN:
      return LINE_CHART_YELLOW;
    case BlazeMetricTypes.RESPONSE_TIME_MAX:
      return LINE_CHART_BLUE;
    case BlazeMetricTypes.RESPONSE_TIME_VALUE:
      return LINE_CHART_GREEN;
    case BlazeMetricTypes.TRANSACTIONS_HITS_COUNT:
    case BlazeMetricTypes.TRANSACTIONS_TOTAL_COUNT:
      return BAR_CHART_BLUE;
    case BlazeMetricTypes.TRANSACTIONS_SECONDS_MIN:
    case BlazeMetricTypes.FORCED_DELAY_MIN:
      return LINE_CHART_YELLOW;
    case BlazeMetricTypes.TRANSACTIONS_MISSES_COUNT:
      return BAR_CHART_ORANGE;
    case BlazeMetricTypes.TRANSACTIONS_SECONDS_MAX:
    case BlazeMetricTypes.FORCED_DELAY_MAX:
      return LINE_CHART_BLUE;
    case BlazeMetricTypes.TRANSACTIONS_SPECIFIC_HITS_COUNT:
      return BAR_CHART_GREEN;
    case BlazeMetricTypes.RESPONSE_TIME_VALUE:
    case BlazeMetricTypes.TRANSACTIONS_SECONDS_VALUE:
    case BlazeMetricTypes.FORCED_DELAY_VALUE:
      return LINE_CHART_GREEN;

    default:
      return BAR_CHART_YELLOW;
  }
};

/**
 * Determines value suffix based on the type of the metric.
 * @param type
 */
export const getValueSuffix = (type: string): string | undefined => {
  switch (type) {
    case BlazeMetricTypes.RESPONSE_TIME_MIN:
    case BlazeMetricTypes.RESPONSE_TIME_MAX:
    case BlazeMetricTypes.RESPONSE_TIME_VALUE:
    case BlazeMetricTypes.FORCED_DELAY_MIN:
    case BlazeMetricTypes.FORCED_DELAY_MAX:
    case BlazeMetricTypes.RESPONSE_TIME_VALUE:
    case BlazeMetricTypes.FORCED_DELAY_VALUE:
      return 'ms';

    default:
      return undefined;
  }
};

/* BZM colors */
export const PASS_COLOR = '#7fc61b';
export const FAIL_COLOR = '#ce222c';
export const BREAK_COLOR = '#e6a200';
export const INFO_COLOR = '#44B3D2';

export const CONNECTIONS_COLOR = '#00ff7f';
export const CPU_COLOR = '#18d0d0';
export const ERRORS_COLOR = '#d01f25';
export const HITS_COLOR = PASS_COLOR;
export const LATENCY_COLOR = '#8a2be2';
export const MEMORY_COLOR = '#0000ff';
export const NETWORK_IO_COLOR = '#ff1493';
export const RESPONSE_TIME_COLOR = '#e6a200';
export const USERS_COLOR = '#5a78f0';
export const STD_RESPONSE_COLOR = '#daa520';

export const PURPLE_JMETER = '#B08ECE';
export const BLUE_MULTI = '#69acb7';
export const GREEN_WEBDRIVER = '#75af75';
export const RED_EXTERNAL = '#D27A92';
export const RED_SLIDING_WINDOW = '#f0dbdc';
export const DURATION_COLOR = '#000';

// Default Chart Options for analytics graph
export const defaultChartOptions: ChartOptions = {
  plotBackgroundColor: PLOT_BACKGROUND_COLOR,
  plotBorderColor: GRID_BORDER_COLOR,
  height: '600px',
  animation: false,
};

export const defaultHeaderFormatDate = '<div class="mb-2"><strong>{point.x:%m/%d/%Y}</strong></div>';
export const defaultHeaderFormatDateTime =
  '<div class="mb-2"><strong>{point.x:%m/%d/%Y} {point.x:%I:%M %p}</strong></div>';

export const defaultTooltip = {
  useHTML: true,
  headerFormat: defaultHeaderFormatDate,
};

export const defaultXAxisFormat = {
  millisecond: '%I:%M:%S.%L %p',
  second: '%I:%M:%S %p',
  minute: '%I:%M %p',
  hour: '%I:%M %p',
  day: "%m/%d '%y",
  week: '%m/%d',
  month: "%b '%y",
  year: '%Y',
};

export const getMetricDisplayText = (type: string, msType: MsType) => {
  switch (type) {
    case BlazeMetricTypes.SERVICE_MOCK_TRANSACTIONS_TOTAL_COUNT:
    case BlazeMetricTypes.TRANSACTIONS_TOTAL_COUNT:
      return i18n.t('analytics:reports.metricsType.total_count');
    case BlazeMetricTypes.TRANSACTIONS_MISSES_COUNT:
      return i18n.t('analytics:reports.metricsType.misses');
    case BlazeMetricTypes.TRANSACTIONS_HITS_COUNT:
      return msType === TRANSACTIONAL
        ? i18n.t('analytics:reports.metricsType.matched_hits')
        : i18n.t('analytics:reports.metricsType.node_hits');
    case BlazeMetricTypes.TRANSACTIONS_SPECIFIC_HITS_COUNT:
      return i18n.t('analytics:reports.metricsType.specific_hits');
    case BlazeMetricTypes.RESPONSE_TIME_MIN:
    case BlazeMetricTypes.TRANSACTIONS_SECONDS_MIN:
    case BlazeMetricTypes.FORCED_DELAY_MIN:
      return i18n.t('analytics:reports.metricsType.minimum');
    case BlazeMetricTypes.RESPONSE_TIME_MAX:
    case BlazeMetricTypes.TRANSACTIONS_SECONDS_MAX:
    case BlazeMetricTypes.FORCED_DELAY_MAX:
      return i18n.t('analytics:reports.metricsType.maximum');
    case BlazeMetricTypes.RESPONSE_TIME_VALUE:
    case BlazeMetricTypes.TRANSACTIONS_SECONDS_VALUE:
    case BlazeMetricTypes.FORCED_DELAY_VALUE:
      return i18n.t('analytics:reports.metricsType.average');
    default:
      return '';
  }
};
